import { EditorContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import styles from "./editor.module.css";
import { editorService, type DraftView } from "./editorService";
import { EXTENSIONS } from "./extensions";
import { extractSharepointLinks, sameLinks, type DetectedLink } from "./sharepointLinks";
import { SourceDocuments } from "./SourceDocuments";

import { Button } from "@/components/Button";
import {
  BulletListIcon,
  ChevronLeftIcon,
  ImageIcon,
  LinkIcon,
  NumberedListIcon,
  TableIcon,
} from "@/components/icons";
import pageHeaderStyles from "@/components/PageHeader.module.css";
import { invalidateNav } from "@/features/manual/api";
import manualStyles from "@/features/manual/manual.module.css";
import { useCurrentUser } from "@/hooks/useCurrentUser";

type SaveState = "saved" | "saving" | "dirty" | "error";

/**
 * The Word-like editing surface for a page's working copy. Opening the editor
 * is read-only until the first change — that's what creates the draft (so
 * browsing never spawns draft versions). Autosaves; submitting sends the
 * draft to the directors and locks editing until approved, rejected, or
 * withdrawn; a draft can also be discarded.
 */
export function EditorPage() {
  const { pageId = "" } = useParams();
  const navigate = useNavigate();
  const { csrfToken } = useCurrentUser();
  const [draft, setDraft] = useState<DraftView | null>(null);
  const [missing, setMissing] = useState(false);
  const [title, setTitle] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  // SharePoint links found in the content — surfaced in SourceDocuments as
  // "not a source yet" suggestions. Recomputed only when the set changes.
  const [contentLinks, setContentLinks] = useState<DetectedLink[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const creating = useRef(false);
  // Which version's content has been fed into the editor — prevents the
  // content effect from clobbering live typing when the draft record changes
  // identity (e.g. when the first edit materialises the draft).
  const loadedFor = useRef<string | null>(null);

  const inReview = draft?.status === "in_review";

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: "",
    editable: true,
    onUpdate: ({ editor: instance }) => {
      setSaveState("dirty");
      const next = extractSharepointLinks(instance.getJSON());
      setContentLinks((prev) => (sameLinks(prev, next) ? prev : next));
    },
  });

  const load = useCallback(() => {
    let cancelled = false;
    editorService
      .getDraft(pageId)
      .then((d) => {
        if (cancelled) return;
        loadedFor.current = null; // force a content feed for the fresh record
        setDraft(d);
        setTitle(d.title);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  useEffect(() => load(), [load]);

  // Feed the loaded document into the editor when both are ready — once per
  // loaded record, never over live edits.
  useEffect(() => {
    if (!editor || !draft) return;
    const key = draft.versionId ?? `published:${draft.pageId}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    editor.commands.setContent(draft.docJson);
    editor.setEditable(draft.status === "draft" || draft.versionId === null);
    setContentLinks(extractSharepointLinks(draft.docJson));
    setSaveState("saved");
  }, [editor, draft]);

  /** The first real edit creates the draft; subsequent saves reuse it. */
  const ensureDraft = useCallback(async (): Promise<DraftView | null> => {
    if (!draft) return null;
    if (draft.versionId) return draft;
    if (creating.current) return null;
    creating.current = true;
    try {
      const created = await editorService.createDraft(pageId, csrfToken ?? "");
      // Adopt the new identity without re-feeding content over live typing.
      loadedFor.current = created.versionId;
      setDraft(created);
      invalidateNav();
      return created;
    } finally {
      creating.current = false;
    }
  }, [draft, pageId, csrfToken]);

  const save = useCallback(async () => {
    if (!editor || !draft || inReview) return;
    setSaveState("saving");
    try {
      const target = await ensureDraft();
      if (!target?.versionId) return;
      const res = await editorService.saveDraft(
        target.versionId,
        title.trim() || "(untitled)",
        editor.getJSON(),
        csrfToken ?? ""
      );
      setSaveState("saved");
      setSavedAt(res.savedAt);
    } catch {
      setSaveState("error");
    }
  }, [editor, draft, inReview, ensureDraft, title, csrfToken]);

  // Debounced autosave whenever the doc or title goes dirty.
  useEffect(() => {
    if (saveState !== "dirty") return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void save(), 1500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [saveState, save]);

  if (missing) {
    return (
      <div className={manualStyles.view}>
        <div className={manualStyles.emptyState}>
          <strong>Page not found.</strong>
          <br />
          The page doesn&rsquo;t exist, or you don&rsquo;t have editing access.
        </div>
      </div>
    );
  }
  if (!draft || !editor) {
    return <div className={`${manualStyles.view} ${manualStyles.quiet}`}>Loading…</div>;
  }

  const submit = async () => {
    // Flush any pending edits first so the reviewed version is what's on screen.
    await save();
    const target = draft.versionId ? draft : null;
    if (!target?.versionId) return;
    setBusy(true);
    try {
      await editorService.submit(target.versionId, summary.trim() || null, csrfToken ?? "");
      invalidateNav();
      setSubmitOpen(false);
      setSummary("");
      load();
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!draft.versionId) return;
    setBusy(true);
    try {
      await editorService.withdraw(draft.versionId, csrfToken ?? "");
      load();
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!draft.versionId) return;
    setBusy(true);
    try {
      const res = await editorService.discardDraft(draft.versionId, csrfToken ?? "");
      invalidateNav();
      if (res.pageDeleted) {
        navigate("/");
        return;
      }
      setDiscardOpen(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/gif,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const asset = await editorService.uploadAsset(file, pageId, csrfToken ?? "");
      editor.chain().focus().setImage({ src: asset.url }).run();
    };
    input.click();
  };

  const openLinkBox = () => {
    setLinkUrl((editor.getAttributes("link").href as string | undefined) ?? "");
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setLinkOpen(false);
  };

  const saveLabel =
    saveState === "saved"
      ? draft.versionId
        ? `Draft saved${savedAt ? ` ${new Date(savedAt).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}` : ""}`
        : "No draft yet"
      : saveState === "saving"
        ? "Saving…"
        : saveState === "error"
          ? "Save failed — retrying on next change"
          : "Unsaved changes";

  const versionHint = inReview
    ? `With the directors for approval. v${draft.versionNo}${draft.publishedVersionNo ? ` · published: v${draft.publishedVersionNo}` : ""}`
    : draft.versionId
      ? `Submitting sends this version to the directors. v${draft.versionNo}${draft.publishedVersionNo ? ` · published: v${draft.publishedVersionNo}` : " · new page"}`
      : "Editing starts a new draft automatically.";

  return (
    <div className={`${manualStyles.view} ${manualStyles.viewWide}`}>
      <div className={pageHeaderStyles.eyebrow}>Content editing</div>
      <input
        className={styles.titleInput}
        value={title}
        placeholder="Page title"
        disabled={inReview}
        onChange={(e) => {
          setTitle(e.target.value);
          setSaveState("dirty");
        }}
        aria-label="Page title"
        title="Click to edit the page title"
      />
      <p className={styles.contextLine}>
        Changes save as a draft automatically.{" "}
        {draft.publishedVersionNo
          ? `Staff keep seeing v${draft.publishedVersionNo} until a director approves this version.`
          : "Staff won't see this page until a director approves it."}
      </p>

      {inReview && (
        <div className={styles.reviewBanner}>
          This version (v{draft.versionNo}) is with the directors for approval. Editing is locked
          until it is approved or rejected.
          <div className={styles.bannerActionRow}>
            <Button variant="secondary" onClick={withdraw} disabled={busy}>
              Withdraw and keep editing
            </Button>
          </div>
        </div>
      )}

      {draft.reviewComment && !inReview && (
        <div className={styles.rejectBanner}>
          <strong>
            Sent back
            {draft.reviewedByName ? ` by ${draft.reviewedByName}` : ""}
            {draft.reviewedAt
              ? ` on ${new Date(draft.reviewedAt).toLocaleDateString("en-AU", { day: "numeric", month: "long" })}`
              : ""}
            :
          </strong>{" "}
          “{draft.reviewComment}”
        </div>
      )}

      {!inReview && (
        <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
          <button
            type="button"
            title="Heading"
            className={editor.isActive("heading", { level: 2 }) ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </button>
          <button
            type="button"
            title="Subheading"
            className={editor.isActive("heading", { level: 3 }) ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </button>
          <span className={styles.sep} />
          <button
            type="button"
            title="Bold"
            className={editor.isActive("bold") ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            title="Italic"
            className={editor.isActive("italic") ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </button>
          <span className={styles.sep} />
          <button
            type="button"
            aria-label="Bulleted list"
            title="Bulleted list"
            className={editor.isActive("bulletList") ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <BulletListIcon />
          </button>
          <button
            type="button"
            aria-label="Numbered list"
            title="Numbered list"
            className={editor.isActive("orderedList") ? styles.on : ""}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <NumberedListIcon />
          </button>
          <span className={styles.sep} />
          <button
            type="button"
            aria-label="Insert table"
            title="Insert table"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            <TableIcon />
          </button>
          <button
            type="button"
            aria-label="Insert image"
            title="Insert image"
            onClick={() => void uploadImage()}
          >
            <ImageIcon />
          </button>
          <button
            type="button"
            aria-label="Link"
            title="Link"
            className={editor.isActive("link") ? styles.on : ""}
            onClick={openLinkBox}
          >
            <LinkIcon />
          </button>
          <span className={styles.spacer} />
          <span className={styles.autosave} data-state={saveState}>
            {saveLabel}
          </span>
        </div>
      )}

      {linkOpen && !inReview && (
        <div className={styles.inlineBox}>
          <label className={styles.inlineLabel} htmlFor="editor-link-url">
            Link URL
          </label>
          <input
            id="editor-link-url"
            type="url"
            value={linkUrl}
            placeholder="https://…"
            className={styles.inlineInput}
            autoFocus
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyLink();
              if (e.key === "Escape") setLinkOpen(false);
            }}
          />
          <div className={styles.inlineActions}>
            <Button variant="primary" onClick={applyLink}>
              {linkUrl.trim() === "" ? "Remove link" : "Apply link"}
            </Button>
            <Button variant="secondary" onClick={() => setLinkOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className={inReview ? `${styles.surface} ${styles.locked}` : styles.surface}>
        <EditorContent editor={editor} className={manualStyles.article} />
      </div>

      {submitOpen && !inReview && (
        <div className={styles.inlineBox}>
          <label className={styles.inlineLabel} htmlFor="editor-change-summary">
            One line on what changed — the directors see this with your submission.
          </label>
          <input
            id="editor-change-summary"
            type="text"
            value={summary}
            maxLength={500}
            placeholder={draft.publishedVersionNo ? "e.g. Updated the invoicing cycle" : "New page"}
            className={styles.inlineInput}
            autoFocus
            onChange={(e) => setSummary(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submit();
              if (e.key === "Escape") setSubmitOpen(false);
            }}
          />
          <div className={styles.inlineActions}>
            <Button variant="primary" onClick={() => void submit()} disabled={busy}>
              Send to the directors
            </Button>
            <Button variant="secondary" onClick={() => setSubmitOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {discardOpen && !inReview && draft.versionId && (
        <div className={styles.inlineBox}>
          <p className={styles.inlineLabel}>
            {draft.publishedVersionNo
              ? `Discard this draft? Staff keep seeing v${draft.publishedVersionNo}; your changes since then are lost.`
              : "Discard this draft? The page has never been published, so the page itself is removed too."}
          </p>
          <div className={styles.inlineActions}>
            <Button variant="danger" onClick={() => void discard()} disabled={busy}>
              Discard draft
            </Button>
            <Button variant="secondary" onClick={() => setDiscardOpen(false)}>
              Keep editing
            </Button>
          </div>
        </div>
      )}

      <SourceDocuments pageId={pageId} csrfToken={csrfToken ?? ""} contentLinks={contentLinks} />

      <div className={styles.actions}>
        {!inReview && draft.versionId && !submitOpen && (
          <Button
            variant="primary"
            onClick={() => {
              setSummary("");
              setSubmitOpen(true);
            }}
            disabled={busy}
          >
            Submit for approval
          </Button>
        )}
        {!inReview && draft.versionId && !discardOpen && (
          <Button variant="secondary" onClick={() => setDiscardOpen(true)} disabled={busy}>
            Discard draft
          </Button>
        )}
        <Link
          to={`/manual/${draft.sectionSlug}/${draft.pageSlug}`}
          onClick={() => invalidateNav()}
          className={manualStyles.editLink}
        >
          <ChevronLeftIcon /> Back to page
        </Link>
        <span className={styles.hint}>{versionHint}</span>
      </div>
    </div>
  );
}
